import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/contexts/AuthContext";
import { ProvinceProvider } from "@/contexts/ProvinceContext";
import useAnalyticsTracking from "@/hooks/useAnalyticsTracking";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Sidebar from "@/components/layout/Sidebar";
import WhatsAppButton from "@/components/layout/WhatsAppButton";

// Public pages
import Home from "@/pages/public/Home";
import NewsList from "@/pages/public/NewsList";
import NewsDetail from "@/pages/public/NewsDetail";
import ColumnistsList from "@/pages/public/ColumnistsList";
import ColumnistDetail from "@/pages/public/ColumnistDetail";
import ColumnDetail from "@/pages/public/ColumnDetail";
import ColumnistPanel from "@/pages/public/ColumnistPanel";
import VideosList, { VideoDetail } from "@/pages/public/VideosList";
import PollsList, { PollDetail } from "@/pages/public/PollsList";
import EventsList from "@/pages/public/EventsList";
import EventDetail from "@/pages/public/EventDetail";
import CityGuide from "@/pages/public/CityGuide";
import Pharmacies from "@/pages/public/Pharmacies";
import TVPage from "@/pages/public/TVPage";
import Login from "@/pages/public/Login";
import Register from "@/pages/public/Register";
import Profile from "@/pages/public/Profile";
import Saved from "@/pages/public/Saved";
import Settings from "@/pages/public/Settings";
import Support from "@/pages/public/Support";
import NewsHotline from "@/pages/public/NewsHotline";
import Corporate from "@/pages/public/Corporate";
import ForgotPassword from "@/pages/public/ForgotPassword";
import ResetPassword from "@/pages/public/ResetPassword";

// Admin pages
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProvinces from "@/pages/admin/AdminProvinces";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminNews from "@/pages/admin/AdminNews";
import AdminTaxonomy from "@/pages/admin/AdminTaxonomy";
import AdminColumnists from "@/pages/admin/AdminColumnists";
import AdminVideos from "@/pages/admin/AdminVideos";
import AdminPolls from "@/pages/admin/AdminPolls";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminCityGuide from "@/pages/admin/AdminCityGuide";
import AdminPharmacies from "@/pages/admin/AdminPharmacies";
import AdminAds from "@/pages/admin/AdminAds";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminNewsTips from "@/pages/admin/AdminNewsTips";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminTV from "@/pages/admin/AdminTV";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminPush from "@/pages/admin/AdminPush";
import AdminPages from "@/pages/admin/AdminPages";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminSocialShare from "@/pages/admin/AdminSocialShare";

function PublicLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useAnalyticsTracking();
  return (
    <>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <Header onToggleSidebar={() => setSidebarOpen(true)} />
      <main className="min-h-[60vh]"><Outlet /></main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <ProvinceProvider>
            <Routes>
              {/* Admin */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="iller" element={<AdminProvinces />} />
                <Route path="kullanicilar" element={<AdminUsers />} />
                <Route path="haberler" element={<AdminNews />} />
                <Route path="kategoriler" element={<AdminTaxonomy />} />
                <Route path="yazarlar" element={<AdminColumnists />} />
                <Route path="videolar" element={<AdminVideos />} />
                <Route path="anketler" element={<AdminPolls />} />
                <Route path="etkinlikler" element={<AdminEvents />} />
                <Route path="sehir-rehberi" element={<AdminCityGuide />} />
                <Route path="eczaneler" element={<AdminPharmacies />} />
                <Route path="reklamlar" element={<AdminAds />} />
                <Route path="anasayfa" element={<AdminHomepage />} />
                <Route path="medya" element={<AdminMedia />} />
                <Route path="haber-hatti" element={<AdminNewsTips />} />
                <Route path="destek" element={<AdminSupport />} />
                <Route path="tv" element={<AdminTV />} />
                <Route path="mail" element={<AdminEmail />} />
                <Route path="bildirimler" element={<AdminPush />} />
                <Route path="sayfalar" element={<AdminPages />} />
                <Route path="analitik" element={<AdminAnalytics />} />
                <Route path="paylasim" element={<AdminSocialShare />} />
                <Route path="ayarlar" element={<AdminSettings />} />
                <Route path="audit" element={<AdminAudit />} />
              </Route>

              {/* Public */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/haberler" element={<NewsList />} />
                <Route path="/haber/:slug" element={<NewsDetail />} />
                <Route path="/kose-yazilari" element={<ColumnistsList />} />
                <Route path="/kose-yazilari/:slug" element={<ColumnistDetail />} />
                <Route path="/kose-yazari" element={<ColumnistPanel />} />
                <Route path="/yazi/:slug" element={<ColumnDetail />} />
                <Route path="/video" element={<VideosList />} />
                <Route path="/video/:id" element={<VideoDetail />} />
                <Route path="/anketler" element={<PollsList />} />
                <Route path="/anketler/:id" element={<PollDetail />} />
                <Route path="/etkinlikler" element={<EventsList />} />
                <Route path="/etkinlikler/:id" element={<EventDetail />} />
                <Route path="/sehir-rehberi" element={<CityGuide />} />
                <Route path="/nobetci-eczaneler" element={<Pharmacies />} />
                <Route path="/tv" element={<TVPage />} />
                <Route path="/giris" element={<Login />} />
                <Route path="/kayit" element={<Register />} />
                <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
                <Route path="/sifre-sifirla" element={<ResetPassword />} />
                <Route path="/profil" element={<Profile />} />
                <Route path="/kaydettiklerim" element={<Saved />} />
                <Route path="/ayarlar" element={<Settings />} />
                <Route path="/destek" element={<Support />} />
                <Route path="/haber-hatti" element={<NewsHotline />} />
                <Route path="/kurumsal/:page" element={<Corporate />} />
              </Route>
            </Routes>
          </ProvinceProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
